import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useNotifications } from "@/context/NotificationContext";
import {
  Plus,
  FileText,
  Search,
  MoreHorizontal,
  Download,
  Send,
  Eye,
  Trash2,
  RefreshCw,
  Bell,
  Palette,
  ArrowRight,
  Loader2,
  X,
  DollarSign,
  Link2,
  Copy,
  Mail
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import UpgradeLimitModal from "@/components/UpgradeLimitModal";

import { API_URL } from "@/utils/api";

export default function InvoicingPage({ user }) {
  const { notify } = useNotifications();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showQuickLook, setShowQuickLook] = useState(false);
  const [quickLookInvoice, setQuickLookInvoice] = useState(null);
  
  // Payment Link state
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkForm, setPaymentLinkForm] = useState({
    client_name: "",
    client_email: "",
    description: "",
    amount: "",
    notes: "",
    send_email: false
  });
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  const [recipients, setRecipients] = useState([]);
  
  // Invoice form state
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: "",
    client_email: "",
    amount: "",
    due_date: "",
    description: "",
    items: [{ description: "", quantity: 1, rate: "" }],
    send_email: false
  });

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState({ feature: "", currentUsage: 0, limit: 0 });

  useEffect(() => {
    fetchInvoices();
    fetchRecipients();
  }, []);

  const fetchInvoices = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/invoices`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(`${API_URL}/api/recipients`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data);
      }
    } catch (err) {
      console.error("Error fetching recipients:", err);
    }
  };

  const handleCreatePaymentLink = async (sendEmail = false) => {
    if (!paymentLinkForm.client_name || !paymentLinkForm.client_email || !paymentLinkForm.description || !paymentLinkForm.amount) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    const amount = parseFloat(paymentLinkForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setCreatingPaymentLink(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/payment-links/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          ...paymentLinkForm,
          amount: amount,
          send_email: sendEmail
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (sendEmail) {
          toast.success("Payment link created and sent to client!");
        } else {
          // Copy to clipboard
          await navigator.clipboard.writeText(data.stripe_url);
          toast.success("Payment link created and copied to clipboard!");
        }
        setShowPaymentLinkModal(false);
        setPaymentLinkForm({
          client_name: "",
          client_email: "",
          description: "",
          amount: "",
          notes: "",
          send_email: false
        });
      } else {
        toast.error(data.detail || "Failed to create payment link");
      }
    } catch (err) {
      console.error("Error creating payment link:", err);
      toast.error("Failed to create payment link");
    } finally {
      setCreatingPaymentLink(false);
    }
  };

  const handleSelectRecipient = (recipient) => {
    setPaymentLinkForm(prev => ({
      ...prev,
      client_name: recipient.name,
      client_email: recipient.email
    }));
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setCreating(true);

    const token = localStorage.getItem("auth_token");
    const headers = { 
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    // Calculate total from items
    const totalAmount = invoiceForm.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) * parseFloat(item.rate || 0));
    }, 0);

    try {
      const res = await fetch(`${API_URL}/api/invoices/create`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          client_name: invoiceForm.client_name,
          client_email: invoiceForm.client_email,
          amount: totalAmount || parseFloat(invoiceForm.amount),
          due_date: invoiceForm.due_date,
          description: invoiceForm.description,
          items: invoiceForm.items.filter(item => item.description && item.rate),
          status: "pending"
        })
      });

      if (res.ok) {
        const newInvoice = await res.json();
        notify.invoiceCreated(newInvoice.invoice_id, totalAmount || parseFloat(invoiceForm.amount));
        toast.success("Invoice created successfully!");
        
        // Auto-send email if selected
        if (invoiceForm.send_email && invoiceForm.client_email) {
          try {
            const emailRes = await fetch(`${API_URL}/api/invoices/${newInvoice.invoice_id}/email`, {
              method: "POST",
              credentials: "include",
              headers,
              body: JSON.stringify({
                recipient_email: invoiceForm.client_email,
                recipient_name: invoiceForm.client_name
              })
            });
            if (emailRes.ok) {
              toast.success(`Invoice sent to ${invoiceForm.client_email}`);
            }
          } catch (emailErr) {
            console.error("Error sending invoice email:", emailErr);
          }
        }
        
        setShowCreateModal(false);
        resetForm();
        fetchInvoices();
      } else {
        const error = await res.json();
        // Check if it's a limit exceeded error
        if (res.status === 403 && error.detail?.error === "limit_exceeded") {
          setLimitInfo({
            feature: error.detail.feature || "invoices",
            currentUsage: error.detail.current || 0,
            limit: error.detail.limit || 0
          });
          setShowCreateModal(false);
          setShowUpgradeModal(true);
        } else {
          toast.error(error.detail?.message || error.detail || "Failed to create invoice");
        }
      }
    } catch (err) {
      toast.error("Failed to create invoice");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    setDownloading(true);
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoice.invoice_id}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice-${invoice.invoice_number || invoice.invoice_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Invoice downloaded!");
      } else {
        toast.error("Failed to download invoice");
      }
    } catch (err) {
      toast.error("Failed to download invoice");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (!invoice.client_email) {
      toast.error("No client email address");
      return;
    }
    
    setSending(true);
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoice.invoice_id}/email`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          recipient_email: invoice.client_email,
          recipient_name: invoice.client_name
        })
      });

      if (res.ok) {
        toast.success(`Invoice sent to ${invoice.client_email}`);
        notify.invoiceSent(invoice.invoice_id, invoice.client_email);
        fetchInvoices();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send invoice");
      }
    } catch (err) {
      toast.error("Failed to send invoice");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm("Are you sure you want to delete this invoice?")) return;

    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success("Invoice deleted");
        fetchInvoices();
      } else {
        toast.error("Failed to delete invoice");
      }
    } catch (err) {
      toast.error("Failed to delete invoice");
    }
  };

  const resetForm = () => {
    setInvoiceForm({
      client_name: "",
      client_email: "",
      amount: "",
      due_date: "",
      description: "",
      items: [{ description: "", quantity: 1, rate: "" }],
      send_email: false
    });
  };

  const addLineItem = () => {
    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, { description: "", quantity: 1, rate: "" }]
    }));
  };

  const removeLineItem = (index) => {
    setInvoiceForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotal = () => {
    return invoiceForm.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 0) * parseFloat(item.rate || 0));
    }, 0);
  };

  const filteredInvoices = invoices.filter(inv => {
    if (activeTab === "sent") return inv.status === "sent";
    if (activeTab === "paid") return inv.status === "paid";
    return true;
  }).filter(inv =>
    inv.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <h1 className="text-2xl font-bold text-[#121212] font-['Manrope']">Invoicing</h1>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-full p-1">
                <TabsTrigger value="all" className="data-[state=active]:bg-[#121212] data-[state=active]:text-white rounded-full">All</TabsTrigger>
                <TabsTrigger value="sent" className="data-[state=active]:bg-[#121212] data-[state=active]:text-white rounded-full">Sent</TabsTrigger>
                <TabsTrigger value="paid" className="data-[state=active]:bg-[#121212] data-[state=active]:text-white rounded-full">Paid</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button 
              onClick={() => setShowPaymentLinkModal(true)}
              variant="outline"
              className="gap-2 w-full sm:w-auto rounded-full px-6 font-semibold border-[#BFFF00] hover:bg-[#BFFF00]/20" 
              data-testid="create-payment-link-btn"
            >
              <Link2 className="w-4 h-4" />
              Payment Link
            </Button>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-[#121212] hover:bg-[#2a2a2a] gap-2 w-full sm:w-auto rounded-full px-6 font-semibold" 
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4" />
              Create Invoice
            </Button>
          </div>
        </div>

        {/* Empty State or Invoice List */}
        {invoices.length === 0 ? (
          <div className="space-y-6">
            <Card className="border-2 border-dashed border-[#BFFF00]/30 bg-[#F7FFE5]/30 rounded-2xl">
              <CardContent className="py-16">
                <div className="text-center max-w-md mx-auto">
                  <div className="w-16 h-16 bg-[#BFFF00]/30 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-[#BFFF00]">
                    <FileText className="w-8 h-8 text-[#121212]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#121212] mb-2 font-['Manrope']">You don't have any invoices yet</h3>
                  <p className="text-[#121212]/60 mb-6">
                    Create your first invoice to start getting paid faster.
                  </p>
                  <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full px-6 font-semibold" 
                    data-testid="create-first-invoice-btn"
                  >
                    Create Invoice
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all cursor-pointer group rounded-2xl">
                <CardContent className="p-5">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center group-hover:bg-[#BFFF00] transition-colors">
                    <RefreshCw className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] mt-4 font-['Manrope']">Recurring Invoices</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Set up automatic invoicing for retainer clients.</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all cursor-pointer group rounded-2xl">
                <CardContent className="p-5">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center group-hover:bg-[#BFFF00] transition-colors">
                    <Bell className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] mt-4 font-['Manrope']">Payment Reminders</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Automatic reminders for overdue invoices.</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all cursor-pointer group rounded-2xl">
                <CardContent className="p-5">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center group-hover:bg-[#BFFF00] transition-colors">
                    <Palette className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] mt-4 font-['Manrope']">Custom Branding</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Add your logo and customize appearance.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#121212]/40" />
                <Input
                  placeholder="Search invoices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-[#F7FFE5] border-[#BFFF00]/30 rounded-xl focus:ring-2 focus:ring-[#BFFF00]/50"
                />
              </div>
            </div>

            <Card className="border-2 border-dashed border-[#BFFF00]/30 overflow-hidden rounded-2xl">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Invoice #</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Client</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Amount</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Due Date</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.invoice_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-[#2563eb]">
                            #{invoice.invoice_number || invoice.invoice_id?.slice(-6)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-gray-900">{invoice.client_name || "Client"}</p>
                            {invoice.client_email && (
                              <p className="text-xs text-gray-500">{invoice.client_email}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{formatCurrency(invoice.amount)}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge 
                            className={
                              invoice.status === "paid" ? "bg-green-100 text-green-700" :
                              invoice.status === "overdue" ? "bg-red-100 text-red-700" :
                              invoice.status === "sent" ? "bg-blue-100 text-blue-700" :
                              "bg-yellow-100 text-yellow-700"
                            }
                          >
                            {invoice.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setQuickLookInvoice(invoice); setShowQuickLook(true); }}>
                                <Eye className="w-4 h-4 mr-2" /> Quick Look
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setSelectedInvoice(invoice); setShowViewModal(true); }}>
                                <FileText className="w-4 h-4 mr-2" /> View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadPDF(invoice)}>
                                <Download className="w-4 h-4 mr-2" /> Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSendInvoice(invoice)}>
                                <Send className="w-4 h-4 mr-2" /> Send to Client
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteInvoice(invoice.invoice_id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-[100]">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Client Name *</Label>
                <Input
                  id="client_name"
                  value={invoiceForm.client_name}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Acme Inc."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_email">Client Email *</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={invoiceForm.client_email}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="billing@acme.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Line Items</Label>
              <div className="space-y-3">
                {invoiceForm.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, "description", e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, "quantity", e.target.value)}
                      className="w-20"
                      min="1"
                    />
                    <Input
                      type="number"
                      placeholder="Rate"
                      value={item.rate}
                      onChange={(e) => updateLineItem(index, "rate", e.target.value)}
                      className="w-28"
                      step="0.01"
                    />
                    {invoiceForm.items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 mr-1" /> Add Line Item
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Notes</Label>
              <Textarea
                id="description"
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Payment terms, additional notes..."
                rows={3}
              />
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(calculateTotal())}</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={invoiceForm.send_email}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, send_email: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-gray-600">Send email to client</span>
                </label>
                <div className="flex gap-2 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCreateModal(false)}
                    className="rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={creating} 
                    className="bg-[#121212] hover:bg-[#2a2a2a] rounded-xl"
                    data-testid="submit-invoice-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {invoiceForm.send_email ? "Create & Send" : "Create Invoice"}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice #{selectedInvoice?.invoice_number || selectedInvoice?.invoice_id?.slice(-6)}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium">{selectedInvoice.client_name}</p>
                  <p className="text-sm text-gray-500">{selectedInvoice.client_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedInvoice.amount)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-medium">
                    {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={
                    selectedInvoice.status === "paid" ? "bg-green-100 text-green-700" :
                    selectedInvoice.status === "sent" ? "bg-blue-100 text-blue-700" :
                    "bg-yellow-100 text-yellow-700"
                  }>
                    {selectedInvoice.status}
                  </Badge>
                </div>
              </div>
              {selectedInvoice.description && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-gray-700">{selectedInvoice.description}</p>
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedInvoice)}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button onClick={() => handleSendInvoice(selectedInvoice)} className="bg-[#2563eb]">
                  <Send className="w-4 h-4 mr-2" /> Send to Client
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Quick Look Modal */}
      <Dialog open={showQuickLook} onOpenChange={setShowQuickLook}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30 p-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#BFFF00]/20 bg-[#F7FFE5]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#121212] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#BFFF00]" />
              </div>
              <div>
                <h3 className="font-bold text-[#121212]">
                  Invoice #{quickLookInvoice?.invoice_number || quickLookInvoice?.invoice_id?.slice(-6)}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm text-[#121212]/60">{quickLookInvoice?.client_name}</span>
                  <Badge className={
                    quickLookInvoice?.status === "paid" ? "bg-green-100 text-green-700 rounded-full" :
                    quickLookInvoice?.status === "sent" ? "bg-blue-100 text-blue-700 rounded-full" :
                    "bg-yellow-100 text-yellow-700 rounded-full"
                  }>
                    {quickLookInvoice?.status}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => quickLookInvoice && handleDownloadPDF(quickLookInvoice)}
                className="rounded-full border-[#BFFF00]/50"
              >
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
              <Button
                size="sm"
                onClick={() => quickLookInvoice && handleSendInvoice(quickLookInvoice)}
                className="rounded-full bg-[#121212]"
              >
                <Send className="w-4 h-4 mr-1" /> Send
              </Button>
            </div>
          </div>

          {/* Preview Area */}
          <div className="flex-1 bg-gray-100 overflow-hidden" style={{ minHeight: '500px' }}>
            {quickLookInvoice?.pdf_filename && (
              <iframe
                src={`${API_URL}/api/invoices/${quickLookInvoice.invoice_id}/pdf#toolbar=1&navpanes=0`}
                className="w-full h-full border-0"
                style={{ minHeight: '500px' }}
                title="Invoice Preview"
              />
            )}
            {!quickLookInvoice?.pdf_filename && (
              <div className="h-full flex items-center justify-center">
                <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                  <FileText className="w-12 h-12 text-[#121212]/40 mx-auto mb-3" />
                  <h4 className="font-bold text-lg text-[#121212] mb-2">PDF not generated</h4>
                  <p className="text-[#121212]/60 text-sm mb-4">This invoice does not have a PDF file yet.</p>
                  <Button
                    onClick={() => quickLookInvoice && handleDownloadPDF(quickLookInvoice)}
                    className="rounded-full bg-[#121212]"
                  >
                    <Download className="w-4 h-4 mr-2" /> Generate & Download
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Link Creation Modal */}
      <Dialog open={showPaymentLinkModal} onOpenChange={setShowPaymentLinkModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope'] flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-[#BFFF00] flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-[#121212]" />
              </div>
              Create Payment Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Recipient Selection */}
            {recipients.length > 0 && (
              <div>
                <Label className="text-[#121212]/60 text-sm">Select Existing Recipient (Optional)</Label>
                <select
                  className="w-full mt-1.5 px-4 py-3 border border-[#121212]/20 rounded-xl bg-white text-sm"
                  onChange={(e) => {
                    const recipient = recipients.find(r => r.recipient_id === e.target.value);
                    if (recipient) handleSelectRecipient(recipient);
                  }}
                  defaultValue=""
                >
                  <option value="">-- Or enter new client below --</option>
                  {recipients.map(r => (
                    <option key={r.recipient_id} value={r.recipient_id}>
                      {r.name} ({r.email}) - {r.type}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#121212]/60 text-sm">Client Name *</Label>
                <Input
                  value={paymentLinkForm.client_name}
                  onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="John Smith"
                  className="mt-1.5 rounded-xl border-[#121212]/20"
                />
              </div>
              <div>
                <Label className="text-[#121212]/60 text-sm">Client Email *</Label>
                <Input
                  type="email"
                  value={paymentLinkForm.client_email}
                  onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="john@company.com"
                  className="mt-1.5 rounded-xl border-[#121212]/20"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#121212]/60 text-sm">Description *</Label>
              <Input
                value={paymentLinkForm.description}
                onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Consulting services - January 2026"
                className="mt-1.5 rounded-xl border-[#121212]/20"
              />
            </div>

            <div>
              <Label className="text-[#121212]/60 text-sm">Amount (USD) *</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#121212]/60">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentLinkForm.amount}
                  onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="pl-8 rounded-xl border-[#121212]/20"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#121212]/60 text-sm">Notes / Remarks (Optional)</Label>
              <Textarea
                value={paymentLinkForm.notes}
                onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional details about this payment..."
                rows={3}
                className="mt-1.5 rounded-xl border-[#121212]/20 resize-none"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => handleCreatePaymentLink(false)}
              disabled={creatingPaymentLink}
              className="flex-1 bg-[#121212] hover:bg-[#2a2a2a] text-white rounded-full"
            >
              {creatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Create & Copy Link
            </Button>
            <Button
              onClick={() => handleCreatePaymentLink(true)}
              disabled={creatingPaymentLink}
              variant="outline"
              className="flex-1 rounded-full border-[#BFFF00] hover:bg-[#BFFF00]/20"
            >
              {creatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Create & Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Limit Modal */}
      <UpgradeLimitModal
        open={showUpgradeModal}
        onClose={setShowUpgradeModal}
        feature={limitInfo.feature}
        currentPlan={user?.plan || "free"}
        currentUsage={limitInfo.currentUsage}
        limit={limitInfo.limit}
      />
    </DashboardLayout>
  );
}
